/* eslint-disable react/forbid-component-props */
import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {AutoSizer, InfiniteLoader, List} from 'react-virtualized';

import {Box} from '@spothero/ui';

import useWindowSize from 'hooks/use-window-size/useWindowSize';

import FacilityCard from './FacilityCard';
import SkeletonCard from './SkeletonCard';
import FacilityListEmpty from './FacilityListEmpty';

/* eslint-disable no-empty-function */
const FacilitiesList = ({
    facilities = [],
    facilityEntities = {},
    loadMoreRows = () => {},
    isMultiSelectEnabled = false,
    showCanonicalGroups,
    errorMessage,
    meta,
    isLoading,
    setScrollPosition,
    selectedTabActive = false,
}) => {
    /* eslint-enable no-empty-function */
    const facilitiesListRef = useRef(null);
    const windowSize = useWindowSize();
    const [listHeight, setListHeight] = useState(0);

    const EMPTY_STATE_TEXT = {
        heading: selectedTabActive
            ? 'Your Selected tab is empty'
            : 'No facilities found',
        body: selectedTabActive
            ? 'Please select a facility from the List (All) tab'
            : 'Try different keywords or remove search filters',
    };

    const isRowLoaded = ({index}) => {
        return Boolean(facilities[index]);
    };

    const calculateRowHeight = ({index}) => {
        const numberOfParkingSpots = facilities[index]?.parkingSpots?.length;
        const expandedHeaderHeight = 48;
        const expandedParkingSpotHeight = 88;
        const spacing = 12;

        const calculatedHeight =
            numberOfParkingSpots * expandedParkingSpotHeight ||
            expandedParkingSpotHeight;

        return showCanonicalGroups
            ? expandedHeaderHeight + calculatedHeight + spacing
            : calculatedHeight + spacing;
    };

    /**
     *
     * @function rowRenderer
     * @param {number} index - The index of the row
     * @param {boolean} [isScrolling] - The List is currently being scrolled
     * @param {boolean} [isVisible] - This row is visible within the List (eg it is not an overscanned row)
     * @param {string} [key] - Unique key within array of rendered rows
     * @param {object} [parent] - Reference to the parent List (instance)
     * @param {object} style - Style object to be applied to row (to position it)
     * @param {number|string} style.height - The height of the row
     * @param {number} style.left - The left position of the row
     * @param {string} style.position - The position of the row
     * @param {number} style.top - The top position of the row
     * @param {number|string} style.width - The width of the row
     * @returns {React.JSX.Element || void} - The rendered row
     */
    const rowRenderer = ({index, key, style}) => {
        // order matters here. This ensures the loading state displays when transitioning from a previously emmpty list
        if (!isRowLoaded({index}) || isLoading) {
            return <SkeletonCard index={index} key={key} style={style} />;
        }

        if (errorMessage && !selectedTabActive) {
            return;
        }

        return (
            <FacilityCard
                key={key}
                renderKey={key}
                facility={facilities[index]}
                facilityEntities={
                    facilities[index]?.parkingSpots &&
                    facilities[index].parkingSpots.map(
                        (id) => facilityEntities[id]
                    )
                }
                index={index}
                isMultiSelectEnabled={isMultiSelectEnabled}
                style={style}
                showCanonicalGroups={showCanonicalGroups}
            />
        );
    };

    const rowCount =
        !selectedTabActive &&
        facilities.length >= meta.limit &&
        meta.nextOffset !== null
            ? facilities.length + 1
            : facilities.length;

    useEffect(() => {
        /* eslint-disable-next-line no-undefined */
        if (windowSize.height === undefined) {
            return;
        }

        const facilitiesListTop =
            facilitiesListRef.current?.getBoundingClientRect().top ?? 0;

        setListHeight(Math.max(0, windowSize.height - facilitiesListTop));
    }, [windowSize, meta.totalFacilityCount]);

    return (
        <Box
            ref={facilitiesListRef}
            data-testid="FacilitiesList-Container"
            marginTop={2}
        >
            <InfiniteLoader
                isRowLoaded={isRowLoaded}
                loadMoreRows={loadMoreRows}
                rowCount={rowCount}
                minimumBatchSize={meta.limit}
                threshold={10}
            >
                {({onRowsRendered, registerChild}) => {
                    return (
                        <AutoSizer style={{height: listHeight}}>
                            {({height, width}) => {
                                return (
                                    <List
                                        key={isLoading}
                                        ref={registerChild}
                                        className="FacilitiesList"
                                        width={width}
                                        height={height}
                                        rowCount={rowCount}
                                        rowHeight={calculateRowHeight}
                                        onRowsRendered={onRowsRendered}
                                        rowRenderer={rowRenderer}
                                        onScroll={({scrollTop}) => {
                                            setScrollPosition(scrollTop);
                                        }}
                                        noRowsRenderer={() => (
                                            <FacilityListEmpty
                                                heading={
                                                    EMPTY_STATE_TEXT.heading
                                                }
                                                body={EMPTY_STATE_TEXT.body}
                                            />
                                        )}
                                    />
                                );
                            }}
                        </AutoSizer>
                    );
                }}
            </InfiniteLoader>
        </Box>
    );
};

FacilitiesList.propTypes = {
    facilityEntities: PropTypes.objectOf(
        PropTypes.shape({
            id: PropTypes.number,
            title: PropTypes.string,
            status: PropTypes.string,
            productOfferings: PropTypes.arrayOf(PropTypes.string),
            canonicalFacilityId: PropTypes.number,
            operatorFacilityId: PropTypes.string,
            canEditTransientRates: PropTypes.bool,
            canEditEventRates: PropTypes.bool,
            canEditEventTiers: PropTypes.bool,
        })
    ),
    isMultiSelectEnabled: PropTypes.bool,
    facilities: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number,
            name: PropTypes.string,
            address: PropTypes.shape({
                streetAddress: PropTypes.string,
                city: PropTypes.string,
                state: PropTypes.string,
                zipcode: PropTypes.string,
            }),
            parkingSpots: PropTypes.arrayOf(PropTypes.number),
        })
    ).isRequired,
    loadMoreRows: PropTypes.func,
    showCanonicalGroups: PropTypes.bool,
    setScrollPosition: PropTypes.func,
    errorMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    meta: PropTypes.shape({
        limit: PropTypes.number,
        nextOffset: PropTypes.number,
        totalFacilityCount: PropTypes.number,
    }),
    isLoading: PropTypes.bool,
    selectedTabActive: PropTypes.bool,
};

export default FacilitiesList;
